import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  isIosDevice,
  isIosNonSafariBrowser,
  isStandalonePwa,
} from '../../push/webPush';
import { STORAGE_KEYS } from '../../utils/constants';

export function InstallPwaBanner() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem(STORAGE_KEYS.PWA_INSTALL_DISMISSED) === '1';
  });
  const ios = useMemo(() => isIosDevice(), []);
  const iosChrome = useMemo(() => isIosNonSafariBrowser(), []);
  const standalone = useMemo(() => isStandalonePwa(), []);

  useEffect(() => {
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferred(e);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  useEffect(() => {
    if (standalone || dismissed) {
      document.documentElement.style.removeProperty('--pwa-banner-offset');
      return undefined;
    }
    if (!deferred && !ios) {
      document.documentElement.style.removeProperty('--pwa-banner-offset');
      return undefined;
    }
    document.documentElement.style.setProperty('--pwa-banner-offset', '72px');
    return () => {
      document.documentElement.style.removeProperty('--pwa-banner-offset');
    };
  }, [standalone, dismissed, deferred, ios]);

  if (standalone || dismissed) return null;
  if (!deferred && !ios) return null;

  function dismiss() {
    localStorage.setItem(STORAGE_KEYS.PWA_INSTALL_DISMISSED, '1');
    setDismissed(true);
  }

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      // ignore
    }
    setDeferred(null);
    dismiss();
  }

  const iosBody = iosChrome
    ? 'Chrome on iPhone cannot install a full PWA. Open this site in Safari, then Share → Add to Home Screen.'
    : 'On iPhone (Safari): Share → Add to Home Screen → open the Prasaar icon → Profile → enable alerts.';

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: { xs: 'max(12px, var(--safe-left))', sm: 24 },
        right: { xs: 'max(12px, var(--safe-right))', sm: 'auto' },
        bottom: 'calc(16px + var(--safe-bottom))',
        zIndex: 1400,
        maxWidth: 420,
        p: 2,
        borderRadius: 3,
        bgcolor: 'secondary.main',
        color: 'common.white',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <GetAppIcon sx={{ mt: 0.25 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            {ios ? 'Add Prasaar to your home screen' : 'Install for instant alerts'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.92, mb: 1.5 }}>
            {ios ? iosBody : 'Add Prasaar to your home screen and never miss a broadcast.'}
          </Typography>
          <Stack direction="row" spacing={1}>
            {deferred && !ios && (
              <Button
                size="small"
                variant="contained"
                onClick={() => void install()}
                sx={{
                  bgcolor: 'common.white',
                  color: 'secondary.main',
                  '&:hover': { bgcolor: 'secondary.light' },
                }}
              >
                Install
              </Button>
            )}
            {ios && (
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  window.dispatchEvent(new Event('prasaar-ios-setup'))
                }
                sx={{
                  bgcolor: 'common.white',
                  color: 'secondary.main',
                  '&:hover': { bgcolor: 'secondary.light' },
                }}
              >
                How to install
              </Button>
            )}
            <Button size="small" color="inherit" onClick={dismiss}>
              Not now
            </Button>
          </Stack>
        </Box>
        <IconButton
          size="small"
          onClick={dismiss}
          sx={{ color: 'common.white' }}
          aria-label="Dismiss"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}
